import { Task, TaskPriority } from '../types';
import { SubTask } from './task-splitter';
import { logger } from '../utils/logger';
import path from 'path';
import fs from 'fs-extra';
import yaml from 'js-yaml';

export interface TaskTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  tags?: string[];
  priority?: TaskPriority;
  estimatedTime?: number;
  subtasks?: SubTask[];
  variables?: Record<string, any>;
  conditions?: {
    when?: string;
    unless?: string;
  };
  metadata?: {
    author?: string;
    version?: string;
    created?: Date;
    updated?: Date;
  };
}

export interface TemplateCategory {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  templates: string[]; // template IDs
}

export class TaskTemplateManager {
  private templates: Map<string, TaskTemplate> = new Map();
  private categories: Map<string, TemplateCategory> = new Map();
  private customTemplatesDir: string;
  private builtinTemplatesDir: string;

  constructor(
    private projectRoot: string = process.cwd()
  ) {
    this.customTemplatesDir = path.join(projectRoot, '.workflow', 'templates');
    this.builtinTemplatesDir = path.join(__dirname, '..', 'templates', 'tasks');
  }

  /**
   * Initialize template system
   */
  async initialize(): Promise<void> {
    // Ensure custom templates directory exists
    await fs.ensureDir(this.customTemplatesDir);
    
    // Load built-in templates
    await this.loadBuiltinTemplates();
    
    // Load custom templates
    await this.loadCustomTemplates();
    
    logger.info(`Loaded ${this.templates.size} task templates`);
  }

  /**
   * Load built-in templates
   */
  private async loadBuiltinTemplates(): Promise<void> {
    const builtinTemplates: TaskTemplate[] = [
      // Feature Development
      {
        id: 'feature-api',
        name: 'API Feature Development',
        category: 'feature',
        description: 'Develop a new API endpoint with tests and documentation',
        tags: ['api', 'backend', 'feature'],
        priority: 'high',
        estimatedTime: 240,
        subtasks: [
          {
            id: '1',
            title: 'API Design & Specification',
            type: 'documentation',
            priority: 'high',
            estimatedTime: 60,
            description: 'Design API endpoints, request/response schemas'
          },
          {
            id: '2',
            title: 'Database Schema Design',
            type: 'feature',
            priority: 'high',
            estimatedTime: 45,
            dependencies: ['1']
          },
          {
            id: '3',
            title: 'API Implementation',
            type: 'feature',
            priority: 'high',
            estimatedTime: 90,
            dependencies: ['2']
          },
          {
            id: '4',
            title: 'Unit Tests',
            type: 'testing',
            priority: 'high',
            estimatedTime: 60,
            dependencies: ['3']
          },
          {
            id: '5',
            title: 'API Documentation',
            type: 'documentation',
            priority: 'medium',
            estimatedTime: 30,
            dependencies: ['3']
          },
          {
            id: '6',
            title: 'Integration Tests',
            type: 'testing',
            priority: 'medium',
            estimatedTime: 45,
            dependencies: ['4']
          }
        ]
      },
      
      // UI Component
      {
        id: 'feature-ui-component',
        name: 'UI Component Development',
        category: 'feature',
        description: 'Create a reusable UI component with tests and documentation',
        tags: ['ui', 'frontend', 'component'],
        priority: 'medium',
        estimatedTime: 180,
        subtasks: [
          {
            id: '1',
            title: 'Component Design',
            type: 'documentation',
            priority: 'high',
            estimatedTime: 30
          },
          {
            id: '2',
            title: 'Component Implementation',
            type: 'feature',
            priority: 'high',
            estimatedTime: 60,
            dependencies: ['1']
          },
          {
            id: '3',
            title: 'Component Tests',
            type: 'testing',
            priority: 'high',
            estimatedTime: 45,
            dependencies: ['2']
          },
          {
            id: '4',
            title: 'Storybook Story',
            type: 'documentation',
            priority: 'medium',
            estimatedTime: 30,
            dependencies: ['2']
          },
          {
            id: '5',
            title: 'Component Documentation',
            type: 'documentation',
            priority: 'medium',
            estimatedTime: 15,
            dependencies: ['2']
          }
        ]
      },

      // Bug Fix Templates
      {
        id: 'bugfix-production',
        name: 'Production Bug Fix',
        category: 'bugfix',
        description: 'Fix a production issue with proper testing and deployment',
        tags: ['bug', 'production', 'hotfix'],
        priority: 'high',
        estimatedTime: 120,
        subtasks: [
          {
            id: '1',
            title: 'Reproduce Issue',
            type: 'research',
            priority: 'high',
            estimatedTime: 30
          },
          {
            id: '2',
            title: 'Root Cause Analysis',
            type: 'research',
            priority: 'high',
            estimatedTime: 30,
            dependencies: ['1']
          },
          {
            id: '3',
            title: 'Implement Fix',
            type: 'bugfix',
            priority: 'high',
            estimatedTime: 30,
            dependencies: ['2']
          },
          {
            id: '4',
            title: 'Add Regression Test',
            type: 'testing',
            priority: 'high',
            estimatedTime: 20,
            dependencies: ['3']
          },
          {
            id: '5',
            title: 'Deploy to Staging',
            type: 'devops',
            priority: 'high',
            estimatedTime: 10,
            dependencies: ['4']
          }
        ]
      },

      // Performance Optimization
      {
        id: 'perf-optimization',
        name: 'Performance Optimization',
        category: 'performance',
        description: 'Identify and fix performance bottlenecks',
        tags: ['performance', 'optimization'],
        priority: 'medium',
        estimatedTime: 240,
        subtasks: [
          {
            id: '1',
            title: 'Performance Profiling',
            type: 'performance',
            priority: 'high',
            estimatedTime: 60
          },
          {
            id: '2',
            title: 'Identify Bottlenecks',
            type: 'research',
            priority: 'high',
            estimatedTime: 45,
            dependencies: ['1']
          },
          {
            id: '3',
            title: 'Implement Optimizations',
            type: 'performance',
            priority: 'high',
            estimatedTime: 90,
            dependencies: ['2']
          },
          {
            id: '4',
            title: 'Performance Testing',
            type: 'testing',
            priority: 'high',
            estimatedTime: 30,
            dependencies: ['3']
          },
          {
            id: '5',
            title: 'Document Changes',
            type: 'documentation',
            priority: 'medium',
            estimatedTime: 15,
            dependencies: ['3']
          }
        ]
      },

      // Security Audit
      {
        id: 'security-audit',
        name: 'Security Audit & Fix',
        category: 'security',
        description: 'Perform security audit and fix vulnerabilities',
        tags: ['security', 'audit'],
        priority: 'high',
        estimatedTime: 300,
        subtasks: [
          {
            id: '1',
            title: 'Dependency Audit',
            type: 'security',
            priority: 'high',
            estimatedTime: 45
          },
          {
            id: '2',
            title: 'Code Security Scan',
            type: 'security',
            priority: 'high',
            estimatedTime: 60
          },
          {
            id: '3',
            title: 'Penetration Testing',
            type: 'testing',
            priority: 'high',
            estimatedTime: 90,
            dependencies: ['1', '2']
          },
          {
            id: '4',
            title: 'Fix Vulnerabilities',
            type: 'security',
            priority: 'high',
            estimatedTime: 60,
            dependencies: ['3']
          },
          {
            id: '5',
            title: 'Security Documentation',
            type: 'documentation',
            priority: 'medium',
            estimatedTime: 45,
            dependencies: ['4']
          }
        ]
      },

      // Documentation
      {
        id: 'docs-api',
        name: 'API Documentation',
        category: 'documentation',
        description: 'Create comprehensive API documentation',
        tags: ['docs', 'api'],
        priority: 'medium',
        estimatedTime: 120,
        subtasks: [
          {
            id: '1',
            title: 'API Inventory',
            type: 'research',
            priority: 'high',
            estimatedTime: 30
          },
          {
            id: '2',
            title: 'Write API Reference',
            type: 'documentation',
            priority: 'high',
            estimatedTime: 60,
            dependencies: ['1']
          },
          {
            id: '3',
            title: 'Create Examples',
            type: 'documentation',
            priority: 'medium',
            estimatedTime: 30,
            dependencies: ['2']
          }
        ]
      }
    ];

    // Register built-in templates
    for (const template of builtinTemplates) {
      this.registerTemplate(template);
    }

    // Register categories
    this.registerCategory({
      id: 'feature',
      name: 'Feature Development',
      description: 'Templates for developing new features',
      icon: '✨'
    });

    this.registerCategory({
      id: 'bugfix',
      name: 'Bug Fixes',
      description: 'Templates for fixing bugs',
      icon: '🐛'
    });

    this.registerCategory({
      id: 'performance',
      name: 'Performance',
      description: 'Templates for performance optimization',
      icon: '⚡'
    });

    this.registerCategory({
      id: 'security',
      name: 'Security',
      description: 'Templates for security tasks',
      icon: '🔒'
    });

    this.registerCategory({
      id: 'documentation',
      name: 'Documentation',
      description: 'Templates for documentation tasks',
      icon: '📚'
    });
  }

  /**
   * Load custom templates from filesystem
   */
  private async loadCustomTemplates(): Promise<void> {
    try {
      const files = await fs.readdir(this.customTemplatesDir);
      
      for (const file of files) {
        if (file.endsWith('.yaml') || file.endsWith('.yml')) {
          const filePath = path.join(this.customTemplatesDir, file);
          const content = await fs.readFile(filePath, 'utf-8');
          const template = yaml.load(content) as TaskTemplate;
          
          if (template && template.id) {
            template.metadata = {
              ...template.metadata,
              updated: new Date()
            };
            this.registerTemplate(template);
            logger.debug(`Loaded custom template: ${template.id}`);
          }
        }
      }
    } catch (error) {
      logger.warn('Failed to load custom templates:', error);
    }
  }

  /**
   * Register a template
   */
  registerTemplate(template: TaskTemplate): void {
    this.templates.set(template.id, template);
    
    // Add to category
    if (template.category) {
      const category = this.categories.get(template.category);
      if (category && !category.templates.includes(template.id)) {
        category.templates.push(template.id);
      }
    }
  }

  /**
   * Register a category
   */
  registerCategory(category: TemplateCategory): void {
    if (!category.templates) {
      category.templates = [];
    }
    this.categories.set(category.id, category);
  }

  /**
   * Get template by ID
   */
  getTemplate(id: string): TaskTemplate | undefined {
    return this.templates.get(id);
  }

  /**
   * Get all templates
   */
  getAllTemplates(): TaskTemplate[] {
    return Array.from(this.templates.values());
  }

  /**
   * Get templates by category
   */
  getTemplatesByCategory(categoryId: string): TaskTemplate[] {
    const category = this.categories.get(categoryId);
    if (!category) return [];
    
    return category.templates
      .map(id => this.templates.get(id))
      .filter(Boolean) as TaskTemplate[];
  }

  /**
   * Get all categories
   */
  getAllCategories(): TemplateCategory[] {
    return Array.from(this.categories.values());
  }

  /**
   * Search templates
   */
  searchTemplates(query: string): TaskTemplate[] {
    const lowercaseQuery = query.toLowerCase();
    
    return this.getAllTemplates().filter(template => 
      template.name.toLowerCase().includes(lowercaseQuery) ||
      template.description?.toLowerCase().includes(lowercaseQuery) ||
      template.tags?.some(tag => tag.toLowerCase().includes(lowercaseQuery))
    );
  }

  /**
   * Create task from template
   */
  createTaskFromTemplate(
    templateId: string, 
    variables?: Record<string, any>
  ): Task & { subtasks?: SubTask[] } {
    const template = this.getTemplate(templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }

    // Replace variables in template
    const processedTemplate = this.processVariables(template, variables);

    // Create task
    const task: Task & { subtasks?: SubTask[] } = {
      id: Date.now().toString(),
      content: processedTemplate.name,
      priority: processedTemplate.priority || 'medium',
      status: 'pending',
      position: 0,
      tags: processedTemplate.tags
    };

    // Add subtasks if available
    if (processedTemplate.subtasks) {
      task.subtasks = processedTemplate.subtasks;
    }

    return task;
  }

  /**
   * Process template variables
   */
  private processVariables(
    template: TaskTemplate,
    variables?: Record<string, any>
  ): TaskTemplate {
    if (!variables || Object.keys(variables).length === 0) {
      return template;
    }

    // Deep clone template
    const processed = JSON.parse(JSON.stringify(template));

    // Replace variables in strings
    const replaceVars = (str: string): string => {
      return str.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
        return variables[varName] || match;
      });
    };

    // Process name and description
    processed.name = replaceVars(processed.name);
    if (processed.description) {
      processed.description = replaceVars(processed.description);
    }

    // Process subtasks
    if (processed.subtasks) {
      processed.subtasks = processed.subtasks.map(subtask => ({
        ...subtask,
        title: replaceVars(subtask.title),
        description: subtask.description ? replaceVars(subtask.description) : undefined
      }));
    }

    return processed;
  }

  /**
   * Save custom template
   */
  async saveTemplate(template: TaskTemplate): Promise<void> {
    const filename = `${template.id}.yaml`;
    const filePath = path.join(this.customTemplatesDir, filename);
    
    template.metadata = {
      ...template.metadata,
      updated: new Date()
    };

    const content = yaml.dump(template, { 
      lineWidth: -1,
      noRefs: true 
    });
    
    await fs.writeFile(filePath, content);
    this.registerTemplate(template);
    
    logger.info(`Saved template: ${template.id}`);
  }

  /**
   * Delete custom template
   */
  async deleteTemplate(id: string): Promise<void> {
    const template = this.getTemplate(id);
    if (!template) {
      throw new Error(`Template not found: ${id}`);
    }

    // Remove from memory
    this.templates.delete(id);
    
    // Remove from categories
    this.categories.forEach(category => {
      const index = category.templates.indexOf(id);
      if (index > -1) {
        category.templates.splice(index, 1);
      }
    });

    // Remove file if it's a custom template
    const filename = `${id}.yaml`;
    const filePath = path.join(this.customTemplatesDir, filename);
    
    if (await fs.pathExists(filePath)) {
      await fs.remove(filePath);
      logger.info(`Deleted template: ${id}`);
    }
  }
}